import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { companiesApi } from '@/api/companies';
import {
  getSchedulerStatus,
  startScheduler,
  stopScheduler,
  runScrapingForCompany,
  runScrapingAll,
  type ScrapingStatusResponse,
} from '@/api/scraping';
import { format } from 'date-fns';
import { Play, Square, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import type { Company } from '@/types/company';

// UTAGE認証情報が完全かチェック
const hasCompleteUtageCredentials = (company: Company): boolean => {
  return !!(company.utage_login_url && company.utage_email && company.has_utage_password);
};

// 不足している認証情報のリストを取得
const getMissingCredentials = (company: Company): string[] => {
  const missing: string[] = [];
  if (!company.utage_login_url) missing.push('ログインURL');
  if (!company.utage_email) missing.push('メールアドレス');
  if (!company.has_utage_password) missing.push('パスワード');
  return missing;
};

export default function Scraping() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [schedulerStatus, setSchedulerStatus] = useState<ScrapingStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [runningCompanyId, setRunningCompanyId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 実行中の法人IDセット（APIから取得）
  const scrapingCompanyIds = new Set(schedulerStatus?.running_company_ids || []);

  const fetchData = async () => {
    try {
      const [companiesRes, statusRes] = await Promise.all([
        companiesApi.getCompanies(),
        getSchedulerStatus(),
      ]);
      setCompanies(companiesRes.items);
      setSchedulerStatus(statusRes);
    } catch (err) {
      console.error('データ取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // 10秒ごとにステータス更新（実行中状態を素早く反映）
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleStartScheduler = async () => {
    setIsActionLoading(true);
    try {
      const res = await startScheduler();
      showMessage('success', res.message);
      fetchData();
    } catch (err) {
      showMessage('error', 'スケジューラーの開始に失敗しました');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStopScheduler = async () => {
    setIsActionLoading(true);
    try {
      const res = await stopScheduler();
      showMessage('success', res.message);
      fetchData();
    } catch (err) {
      showMessage('error', 'スケジューラーの停止に失敗しました');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRunAll = async () => {
    if (!confirm('全法人のスクレイピングを開始しますか？')) return;
    setIsActionLoading(true);
    try {
      const res = await runScrapingAll();
      showMessage('success', res.message);
    } catch (err) {
      showMessage('error', 'スクレイピングの開始に失敗しました');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRunCompany = async (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    const companyName = company?.display_name || `法人ID ${companyId}`;

    setRunningCompanyId(companyId);
    try {
      await runScrapingForCompany(companyId);
      showMessage('success', `「${companyName}」のスクレイピングを開始しました（バックグラウンドで実行中）`);
      // ステータスを即座に更新して実行中状態を反映
      fetchData();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 'スクレイピング開始に失敗しました';
      showMessage('error', `「${companyName}」: ${errorMessage}`);
    } finally {
      setRunningCompanyId(null);
    }
  };

  // モバイル用: 法人カード
  const CompanyCard = ({ company }: { company: Company }) => {
    const hasCredentials = hasCompleteUtageCredentials(company);
    const missingList = getMissingCredentials(company);
    const isScraping = scrapingCompanyIds.has(company.id);

    return (
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{company.display_name}</h3>
            {isScraping && (
              <Badge variant="info" className="animate-pulse">
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                実行中
              </Badge>
            )}
          </div>
          <Badge variant={company.is_active ? 'success' : 'default'}>
            {company.is_active ? '有効' : '無効'}
          </Badge>
        </div>

        {!hasCredentials && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">UTAGE認証情報が不足</div>
              <div className="text-xs">{missingList.join('、')}</div>
            </div>
          </div>
        )}

        <div className="text-sm">
          <span className="text-gray-500">最終データ取得: </span>
          <span className="text-gray-900">
            {company.last_scraped_at
              ? format(new Date(company.last_scraped_at), 'yyyy/MM/dd HH:mm')
              : '未取得'}
          </span>
        </div>

        <Button
          size="sm"
          variant={isScraping ? 'primary' : 'outline'}
          onClick={() => handleRunCompany(company.id)}
          isLoading={runningCompanyId === company.id}
          disabled={!company.is_active || !hasCredentials || runningCompanyId !== null || isScraping}
          className="w-full"
        >
          {isScraping ? (
            <>
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              実行中...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-1" />
              スクレイピング実行
            </>
          )}
        </Button>
      </div>
    );
  };

  // モバイル用: ジョブカード
  const JobCard = ({ job }: { job: { id: string; name: string; next_run_time: string | null } }) => (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-1">
      <div className="font-medium text-gray-900">{job.name}</div>
      <div className="text-sm text-gray-500 font-mono">{job.id}</div>
      <div className="text-sm flex items-center gap-1">
        <Clock className="w-4 h-4 text-gray-400" />
        <span>
          {job.next_run_time
            ? format(new Date(job.next_run_time), 'yyyy/MM/dd HH:mm:ss')
            : '-'}
        </span>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">スクレイピング管理</h1>
        <p className="text-sm sm:text-base text-gray-500">UTAGEからのデータ収集を管理します</p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* スケジューラーステータス */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>スケジューラー</span>
            <Badge variant={schedulerStatus?.running ? 'success' : 'default'}>
              {schedulerStatus?.running ? '実行中' : '停止中'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              {schedulerStatus?.running ? (
                <Button
                  variant="danger"
                  onClick={handleStopScheduler}
                  isLoading={isActionLoading}
                  className="w-full sm:w-auto"
                >
                  <Square className="w-4 h-4 mr-1" />
                  スケジューラー停止
                </Button>
              ) : (
                <Button
                  onClick={handleStartScheduler}
                  isLoading={isActionLoading}
                  className="w-full sm:w-auto"
                >
                  <Play className="w-4 h-4 mr-1" />
                  スケジューラー開始
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleRunAll}
                isLoading={isActionLoading}
                className="w-full sm:w-auto"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                今すぐ全法人実行
              </Button>
            </div>

            {schedulerStatus?.jobs && schedulerStatus.jobs.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">スケジュール済みジョブ</h4>

                {/* デスクトップ: テーブル */}
                <div className="hidden sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ジョブID</TableHead>
                        <TableHead>ジョブ名</TableHead>
                        <TableHead>次回実行</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedulerStatus.jobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="font-mono text-sm">{job.id}</TableCell>
                          <TableCell>{job.name}</TableCell>
                          <TableCell>
                            {job.next_run_time
                              ? format(new Date(job.next_run_time), 'yyyy/MM/dd HH:mm:ss')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* モバイル: カード */}
                <div className="sm:hidden space-y-2">
                  {schedulerStatus.jobs.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 法人別スクレイピング */}
      <Card>
        <CardHeader>
          <CardTitle>法人別スクレイピング</CardTitle>
        </CardHeader>
        <CardContent>
          {companies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              法人が登録されていません
            </div>
          ) : (
            <>
              {/* デスクトップ: テーブル */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>法人名</TableHead>
                      <TableHead>ステータス</TableHead>
                      <TableHead>UTAGE認証</TableHead>
                      <TableHead>最終データ取得</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((company) => {
                      const hasCredentials = hasCompleteUtageCredentials(company);
                      const missingList = getMissingCredentials(company);
                      const isScraping = scrapingCompanyIds.has(company.id);
                      return (
                        <TableRow key={company.id} className={isScraping ? 'bg-blue-50' : undefined}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {company.display_name}
                              {isScraping && (
                                <Badge variant="info" className="animate-pulse">
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  実行中
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={company.is_active ? 'success' : 'default'}>
                              {company.is_active ? '有効' : '無効'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {hasCredentials ? (
                              <Badge variant="success">設定済み</Badge>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Badge variant="warning" className="flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  未設定
                                </Badge>
                                <span className="text-xs text-gray-500" title={missingList.join('、')}>
                                  ({missingList.join('、')})
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {company.last_scraped_at
                              ? format(new Date(company.last_scraped_at), 'yyyy/MM/dd HH:mm')
                              : '未取得'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant={isScraping ? 'primary' : 'outline'}
                              onClick={() => handleRunCompany(company.id)}
                              isLoading={runningCompanyId === company.id}
                              disabled={!company.is_active || !hasCredentials || runningCompanyId !== null || isScraping}
                              title={!hasCredentials ? 'UTAGE認証情報を設定してください' : undefined}
                            >
                              {isScraping ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  実行中
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-1" />
                                  実行
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* モバイル: カード */}
              <div className="md:hidden space-y-4">
                {companies.map((company) => (
                  <CompanyCard key={company.id} company={company} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
